import { usePeriod } from '../../payload/PeriodContext'
import { CHART_HEIGHT, chartTheme } from '../charts/chartTheme'
import { buildBarsOption } from '../charts/countsOptions'
import EChart from '../charts/EChart'
import {
  BarsH,
  Card,
  CardHead,
  DataTable,
  EmptyState,
  Grid,
  HelpMark,
  InlineBar,
  Note,
  SectionHead,
  Td,
  Tr,
} from '../components'
import { biggest, byKey, monthRange, n, num, plural, s } from '../format'
import { useBlank } from './useBlank'

/** How many do we really have, and how complete is their financial picture. */
const Users = () => {
  const { one, panel, series } = usePeriod()
  const blank = useBlank()
  const o = one('overview')
  const classes = panel('user_classes')
  const breadth = panel('breadth')
  const topics = panel('topic_coverage')

  const classMax = Math.max(1, ...classes.map(c => n(c.users)))
  const notReal = n(o.registered_accounts) - n(o.real_users)
  const internal = byKey(classes, 'user_class', 'internal')

  // Who actually holds the most complete profile, and how far behind the best
  // real user is. Stated as a comparison so it survives either side changing.
  const deepestOther = biggest(
    classes.filter(c => c.user_class !== 'real'),
    'deepest_profile_ever'
  )
  const deepestReal = byKey(classes, 'user_class', 'real')

  const signups = series('users_weekly', 'users_daily')
  const signupsTitle =
    signups.grain === 'day'
      ? 'New real users, by day'
      : 'New real users, by week'

  // The prototype typed "Fifty-four accounts exist" into this heading; the
  // number is the payload's now, all time like the tile it echoes, and the
  // second clause only holds while our own accounts really are the majority.
  const everAll = n(o.registered_accounts_ever)
  const oursAreMost = everAll - n(o.real_users_ever) > everAll / 2
  const question = everAll
    ? `How many do we really have? ${plural(everAll, 'account')} exist${oursAreMost ? '; most of them are ours' : ''}.`
    : 'How many do we really have?'

  return (
    <section className="grid gap-4">
      <SectionHead title="Users" question={question} />

      <Card>
        <CardHead
          title="Who our accounts belong to"
          info={[
            `Each account is in exactly one group and the groups add up, so you can see where the other ${plural(notReal, 'account')} went rather than taking ${num(o.real_users)} on trust. Click the ? on a row for what its group means.`,
            `Real users are the headline figure: an own email address, no staff domain, no tester flag, and seen in the US. ${num(o.real_users_ever)} of ${num(o.registered_accounts_ever)} accounts all time.`,
            'The signups from outside the US were checked by hand and are all ours, so this is not a floor with a higher ceiling above it — it is the count.',
          ]}
        />
        {classes.length ? (
          <DataTable
            label="Who our accounts belong to"
            head={[
              { label: 'Account group' },
              { label: '' },
              { label: 'Accounts', num: true },
              { label: 'Entered data', num: true },
              { label: '3+ topics', num: true },
              { label: 'Answers', num: true },
              { label: 'Joined' },
            ]}
          >
            {classes.map(c => (
              <Tr key={s(c.user_class)} highlight={c.user_class === 'real'}>
                <Td>
                  {s(c.class_label)}
                  <HelpMark text={s(c.class_means)} />
                </Td>
                <Td>
                  <InlineBar
                    value={n(c.users)}
                    max={classMax}
                    alt={c.user_class !== 'real'}
                    hint={`${plural(n(c.users), 'account')} of ${num(o.registered_accounts)} active`}
                  />
                </Td>
                <Td num>{num(c.users)}</Td>
                <Td num>{num(c.with_any_data)}</Td>
                <Td num>{num(c.with_three_topics)}</Td>
                <Td num muted>
                  {num(c.submissions)}
                </Td>
                <Td muted nowrap>
                  {monthRange(c.first_signup, c.last_signup)}
                </Td>
              </Tr>
            ))}
          </DataTable>
        ) : (
          blank('user_classes', 'No accounts were active in this period.')
        )}
        <Note
          parts={
            internal &&
            n(internal.geo_accounts) > 0 && [
              <strong key="lead">
                Part of that group is judged on country, not on a work
                email.{' '}
              </strong>,
              `${plural(n(internal.geo_accounts), 'account')} of the ${num(internal.users)} are ours on geography alone — Zealth only handles US taxes and the team works from Ukraine, Poland and Portugal. All of them have been checked by hand and confirmed, but the rule is a prior rather than proof, so a genuine non-US customer signing up tomorrow would land here and need moving out.`,
            ]
          }
        />
      </Card>

      <Grid cols={2}>
        <Card>
          <CardHead
            title={signupsTitle}
            info={[
              'Counted in the period they signed up. "Went on to enter data" is counted whenever they got round to it rather than inside that period, so recent bars can only improve.',
              'Anchored on when the household profile was created, because that is the only signup timestamp we hold.',
            ]}
          />
          {signups.rows.length ? (
            <EChart
              height={CHART_HEIGHT.bars}
              option={buildBarsOption({
                rows: signups.rows,
                xKey: signups.xKey,
                grain: signups.grain,
                label: signupsTitle,
                series: [
                  {
                    key: 'real_signups',
                    label: 'Signed up',
                    color: chartTheme.series[0],
                  },
                  {
                    key: 'real_activated',
                    label: 'Went on to enter data',
                    color: chartTheme.series[1],
                  },
                ],
              })}
            />
          ) : (
            <EmptyState text="No signups in this period." />
          )}
        </Card>

        <Card>
          <CardHead
            title="How complete is their financial picture?"
            info={[
              `Topics filled in per account, out of ${num(topics.length)} the product offers. A projection is only worth showing to someone near the bottom of this list.`,
              'The two series must not be added together: one is the classified real users, the other pools team, test and early web-app accounts.',
              deepestOther &&
                deepestReal &&
                n(deepestOther.deepest_profile_ever) >
                  n(deepestReal.deepest_profile_ever) &&
                `The most complete profile in the database — ${plural(n(deepestOther.deepest_profile_ever), 'topic')} — belongs to the "${s(deepestOther.class_label)}" group. No real user has gone past ${num(deepestReal.deepest_profile_ever)}.`,
            ]}
          />
          {breadth.length ? (
            <BarsH
              rows={breadth}
              labelKey="bucket"
              label="How complete their finances are"
              labelWidth={180}
              series={[
                {
                  key: 'real_users',
                  label: 'Real users',
                  color: chartTheme.series[0],
                },
                {
                  key: 'other_accounts',
                  label: 'Team, tests and early signups',
                  color: chartTheme.series[1],
                },
              ]}
            />
          ) : (
            blank('breadth', 'No accounts were active in this period.')
          )}
        </Card>
      </Grid>
    </section>
  )
}

export default Users
